import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/system';
import { isEmpty, isNil } from 'lodash';
import { CAP_TABLE_CURRENCY_PAGE } from 'common/constants/currencyPageTypes';
import {
  FUND_INVESTMENT_SUMMARY_REFERENCE_TITLE,
  FUND_INVESTMENT_SUMMARY_REFERENCE_TYPE,
} from 'common/constants/documents';
import { FUND_INVESTMENT_SUMMARY_PAGE_KEY, FUND_INVESTMENT_SUMMARY_PAGE_VALUE } from 'common/constants/notes';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { UseFormatValues } from 'common/types/format';
import { ScalarSpreadsheetProps } from 'common/types/scalarSpreadsheet';
import { UseStoreValues } from 'common/types/store';
import { CustomContainer, CustomScalarSpreadsheet, SectionHeading } from 'components';
import { ExtendedFabButton } from 'components/Buttons';
import WidgetDrawer from 'components/Drawer/widget-drawer/WidgetDrawer';
import { WidgetDrawerApplicationContext } from 'components/Drawer/widget-drawer/WidgetDrawer.props';
import { REGULAR_UNIT } from 'components/FeaturedSpreadsheet/constants';
import { GridSkeleton } from 'components/Grid';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkbookContext from 'components/ScalarSpreadsheet/utilities/WorkbookContext';
import { useFundInvestmentContext } from 'context';
import { useSynchronizedState } from 'hooks';
import { FundInvestmentDetailFieldAttributes, FundInvestmentPayload, Option } from 'pages/FundInvestments';
import {
  FUND_INVESTMENTS_ADD_INVESTMENT_VEHICLE_SLUG,
  FUND_INVESTMENTS_ADD_INVESTMENT_VEHICLE_TITLE,
  FUND_INVESTMENTS_CONTAINER_CLASSNAME,
  FUND_INVESTMENTS_DEFAULT_CURRENCY,
  FUND_INVESTMENTS_FUND_INVESTMENT_DETAIL_FIELD_ATTRIBUTES,
  FUND_INVESTMENTS_SUMMARY_CONTAINER_CLASSNAME,
  FUND_INVESTMENTS_SUMMARY_TITLE,
  getFundInvestmentNewInvestmentVehicle,
} from 'pages/FundInvestments/common/constants';
import { useFundInvestmentLayout, useFundInvestmentSummarySheet } from 'pages/FundInvestments/hooks';
import { FundInvestmentSummaryCellData } from 'pages/FundInvestments/hooks/useFundInvestmentSummarySheet';
import { getFundOptions, parseFundInvestmentDetails } from 'pages/FundInvestments/utils';
import { useDocuments } from 'services/hooks';
import { useNotes } from 'services/hooks/notes';
import { useAllocationValuesStore } from 'store';
import {
  getArrayValue,
  getBooleanValue,
  getNumberValue,
  getObjectValue,
  getStringValue,
  handleCatchError,
} from 'utilities';
import { DeletedFundInvestmentDetails, FundInvestmentDetails } from './FundInvestmentSummary.types';

const FundInvestmentSummary: FC = () => {
  const [storeValue] = useStore() as UseStoreValues;
  const { companyInfo: currentFundInvestment, fieldAttributes, fundList: currentFunds } = getObjectValue(storeValue);
  const { captable_currency: currentCurrency } = getObjectValue(currentFundInvestment);

  // Documents
  const { filesToSave, setFilesToSave, addReferenceForExistingDocuments } = useDocuments();

  // Notes
  const { notes, notesHasChanged, onAddNote, onDeleteNote, onUpdateNotes, saveNotes, setNotes } = useNotes();

  // Allocation store
  const currentAllocationId = useAllocationValuesStore(state => state.currentAllocationId);

  // Fund investments context
  const {
    fundInvestmentAllocationStatus,
    fundInvestmentSpreadsheet,
    fundInvestmentValuationAllocation,
    fundInvestmentValuationOwnership,
  } = useFundInvestmentContext();
  const { isCurrentValuationDisabled } = fundInvestmentAllocationStatus;
  const {
    areAllCellsValid,
    cleanUnsavedChanges,
    hasInvalidFundSelections,
    isValidating,
    setArePendingChanges,
    setHasInvalidFundSelections,
    setHasValidationErrors,
    setIsValidating,
  } = fundInvestmentSpreadsheet;
  const {
    currentMeasurementDate,
    fundInvestmentByAllocation,
    isFetchingFundInvestmentByAllocation,
    isLoadingFundInvestmentByAllocation,
    isUpdatingFundInvestmentDetails,
    isUpdatingValuation,
    selectedMeasurementDate,
    updateFundInvestmentDetails,
  } = fundInvestmentValuationAllocation;
  const { currentFundInvestmentOwnershipId } = fundInvestmentValuationOwnership;

  // Fund investments values
  const [currentDeletedFundInvestmentDetails, setCurrentDeletedFundInvestmentDetails] =
    useState<DeletedFundInvestmentDetails>(null);
  const [currentFundInvestmentDetails, setCurrentFundInvestmentDetails] = useState<FundInvestmentDetails>(null);

  // Synchronized states
  const [fundInvestmentDetailsForSheet, setFundInvestmentDetailsForSheet, fundInvestmentDetailsRef] =
    useSynchronizedState<FundInvestmentDetails>({
      initialValue: null,
      syncRefWith: currentFundInvestmentDetails,
    });

  const currentFundOptions = useMemo<Option[]>(() => getFundOptions(currentFunds), [currentFunds]);

  const fundInvestmentDetailsCount = useMemo<number>(
    () => getNumberValue(currentFundInvestmentDetails?.length),
    [currentFundInvestmentDetails]
  );

  const fundInvestmentDetailFieldAttributes = useMemo<FundInvestmentDetailFieldAttributes>(
    () =>
      getObjectValue(
        fieldAttributes?.[FUND_INVESTMENTS_FUND_INVESTMENT_DETAIL_FIELD_ATTRIBUTES]
      ) as FundInvestmentDetailFieldAttributes,
    [fieldAttributes]
  );

  const isLoading = useMemo<boolean>(
    () => isLoadingFundInvestmentByAllocation || isNil(currentAllocationId) || isNil(selectedMeasurementDate),
    [currentAllocationId, isLoadingFundInvestmentByAllocation, selectedMeasurementDate]
  );

  // Scalar spreadsheet
  const { spreadsheet: fundInvestmentSummarySheet } = useFundInvestmentSummarySheet({
    fieldAttributes: fundInvestmentDetailFieldAttributes,
    forceReadOnly: isCurrentValuationDisabled || isUpdatingFundInvestmentDetails || isUpdatingValuation,
    fundInvestmentDetails: fundInvestmentDetailsForSheet,
    fundOptions: currentFundOptions,
    measurementDate: getStringValue(currentMeasurementDate),
    setFundInvestmentDetails: setCurrentFundInvestmentDetails,
  });

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore: create global Market adjustment SpreadsheetConfig type
  const spreadsheets = useMemo<SpreadsheetConfig[]>(
    () => (fundInvestmentSummarySheet?.yLength ? [fundInvestmentSummarySheet] : []),
    [fundInvestmentSummarySheet]
  );

  const { areCellsValid, onChange, workbook, workbookContextValue } = useWorkbook(spreadsheets);

  const [format, formatDispatch] = useFormat({
    page: CAP_TABLE_CURRENCY_PAGE,
    sourceCurrency: currentCurrency ?? FUND_INVESTMENTS_DEFAULT_CURRENCY,
    units: REGULAR_UNIT,
  }) as UseFormatValues;

  const addInvestmentVehicle = useCallback(() => {
    if (currentFundInvestmentOwnershipId) {
      setFundInvestmentDetailsForSheet([
        ...getArrayValue(fundInvestmentDetailsRef.current),
        getFundInvestmentNewInvestmentVehicle(currentFundInvestmentOwnershipId),
      ]);

      // Mark changes as pending
      setArePendingChanges(true);
    }
  }, [
    currentFundInvestmentOwnershipId,
    fundInvestmentDetailsRef,
    setArePendingChanges,
    setFundInvestmentDetailsForSheet,
  ]);

  const removeInvestmentVehicle = useCallback(
    (cellData: FundInvestmentSummaryCellData) => {
      const { fundInvestmentId, fundInvestmentUuid } = getObjectValue(cellData);

      setFundInvestmentDetailsForSheet(() => {
        const updatedDetails = getArrayValue(fundInvestmentDetailsRef.current).filter(
          investment => investment?.uuid !== fundInvestmentUuid
        );

        // If the fund investment is deleted, add it to the deleted details
        if (fundInvestmentId) {
          setCurrentDeletedFundInvestmentDetails(prevDeletedState =>
            Array.from(new Set([...getArrayValue(prevDeletedState), fundInvestmentId]))
          );

          // Mark changes as pending
          setArePendingChanges(true);
        }

        return updatedDetails;
      });
    },
    [fundInvestmentDetailsRef, setArePendingChanges, setFundInvestmentDetailsForSheet]
  );

  const saveFundInvestment = useCallback(async () => {
    setIsValidating(true);

    if (!isEmpty(filesToSave) && selectedMeasurementDate?.id) {
      addReferenceForExistingDocuments(
        selectedMeasurementDate.id,
        filesToSave,
        currentFundInvestmentOwnershipId as number,
        FUND_INVESTMENT_SUMMARY_REFERENCE_TYPE
      );
    }

    if (!isEmpty(notes) && notesHasChanged) {
      saveNotes();
    }

    const hasInvalidCells = (!isNil(areAllCellsValid) && !areAllCellsValid()) || hasInvalidFundSelections;
    setHasValidationErrors(hasInvalidCells);

    // If there are invalid cells or funds, do not proceed with saving
    if (hasInvalidCells) {
      setIsValidating(false);
      return;
    }

    const updatedFundInvestment = {
      allocation: getNumberValue(currentAllocationId),
      deleted_investment_details: getArrayValue(currentDeletedFundInvestmentDetails),
      investment_details: getArrayValue(currentFundInvestmentDetails),
    } as FundInvestmentPayload;

    try {
      const fundInvestmentResponse = await updateFundInvestmentDetails({
        fundInvestmentDetails: updatedFundInvestment,
      });

      const { fundInvestment: updatedFundInvestmentDetails } = getObjectValue(fundInvestmentResponse);

      if (isEmpty(updatedFundInvestmentDetails)) {
        return;
      }

      // Mark changes as not pending
      cleanUnsavedChanges();
      setArePendingChanges(false);

      // Set the updated fund investment details
      setFundInvestmentDetailsForSheet(parseFundInvestmentDetails(updatedFundInvestmentDetails));
    } catch (error) {
      handleCatchError(error);
    }

    setIsValidating(false);
  }, [
    addReferenceForExistingDocuments,
    areAllCellsValid,
    cleanUnsavedChanges,
    currentAllocationId,
    currentDeletedFundInvestmentDetails,
    currentFundInvestmentOwnershipId,
    currentFundInvestmentDetails,
    filesToSave,
    hasInvalidFundSelections,
    notes,
    notesHasChanged,
    saveNotes,
    selectedMeasurementDate,
    setArePendingChanges,
    setFundInvestmentDetailsForSheet,
    setHasValidationErrors,
    setIsValidating,
    updateFundInvestmentDetails,
  ]);

  const fundInvestmentSummarySheetProps = useMemo<ScalarSpreadsheetProps | null>(() => {
    if (fundInvestmentSummarySheet) {
      return {
        ...fundInvestmentSummarySheet,
        allowDeleteRow: fundInvestmentDetailsCount > 1 && !isCurrentValuationDisabled, // Allow deleting only if there is more than one investment vehicle and not disabled
        data: getArrayValue(fundInvestmentSummarySheet?.data),
        deleteRowFn: removeInvestmentVehicle,
        format,
        formatDispatch,
        onChange,
        sheet: fundInvestmentSummarySheet,
        workbook,
      } as unknown as ScalarSpreadsheetProps;
    }

    return null;
  }, [
    format,
    formatDispatch,
    fundInvestmentDetailsCount,
    fundInvestmentSummarySheet,
    isCurrentValuationDisabled,
    onChange,
    removeInvestmentVehicle,
    workbook,
  ]);

  // Higher-Order Function to check cell validation
  const validateAllCells = useCallback(
    () => () => {
      // Check if there are any invalid funds
      const hasInvalidFunds = getBooleanValue(
        currentFundInvestmentDetails?.some(fundInvestment => !fundInvestment?.fund)
      );

      setHasInvalidFundSelections(hasInvalidFunds);

      return areCellsValid(false, true) as boolean;
    },
    [areCellsValid, currentFundInvestmentDetails, setHasInvalidFundSelections]
  );

  // Summary layout
  useFundInvestmentLayout({
    isLoading,
    onSave: saveFundInvestment,
    pageTitle: FUND_INVESTMENTS_SUMMARY_TITLE,
    showMarketDateOnFilter: false,
    showVersionFilter: true,
    validateAllCells,
  });

  // Set current fund investment details
  useEffect(() => {
    if (!isLoading && !isNil(fundInvestmentByAllocation) && currentFundInvestmentOwnershipId) {
      setFundInvestmentDetailsForSheet(
        isEmpty(fundInvestmentByAllocation)
          ? [getFundInvestmentNewInvestmentVehicle(currentFundInvestmentOwnershipId)]
          : parseFundInvestmentDetails(fundInvestmentByAllocation)
      );
    }

    // Cleanup when dependencies change or on unmount
    return () => {
      setFundInvestmentDetailsForSheet(null);
    };
  }, [currentFundInvestmentOwnershipId, fundInvestmentByAllocation, isLoading, setFundInvestmentDetailsForSheet]);

  // Synchronize current fund investment details with the sheet data
  useEffect(() => {
    setCurrentFundInvestmentDetails(fundInvestmentDetailsForSheet);

    // Cleanup when dependencies change or on unmount
    return () => {
      setCurrentFundInvestmentDetails(null);
    };
  }, [fundInvestmentDetailsForSheet, setCurrentFundInvestmentDetails]);

  return (
    <CustomContainer className={FUND_INVESTMENTS_CONTAINER_CLASSNAME}>
      <Grid className={FUND_INVESTMENTS_SUMMARY_CONTAINER_CLASSNAME} size={{ xs: 12 }}>
        <SectionHeading leftAligned title={FUND_INVESTMENTS_SUMMARY_TITLE} />

        {isLoading ? (
          //  Loading grid skeleton while fetching data
          <GridSkeleton maxColumns={10} maxRows={4} />
        ) : (
          fundInvestmentSummarySheetProps &&
          fundInvestmentSummarySheet &&
          !isEmpty(fundInvestmentDetailsForSheet) && (
            <>
              <WorkbookContext.Provider value={workbookContextValue}>
                <CustomScalarSpreadsheet
                  {...fundInvestmentSummarySheetProps}
                  isLoading={isFetchingFundInvestmentByAllocation}
                  isSaving={isUpdatingFundInvestmentDetails || isValidating}
                />
              </WorkbookContext.Provider>

              {/* Add Investment Vehicle */}
              <ExtendedFabButton
                addBottomMarginForDrawer
                disabled={isCurrentValuationDisabled || isUpdatingFundInvestmentDetails}
                id={FUND_INVESTMENTS_ADD_INVESTMENT_VEHICLE_SLUG}
                label={FUND_INVESTMENTS_ADD_INVESTMENT_VEHICLE_TITLE}
                onClick={addInvestmentVehicle}
              />

              {/* Notes and documents drawer */}
              {!isNil(currentFundInvestmentOwnershipId) && (
                <WidgetDrawer
                  applicationContext={WidgetDrawerApplicationContext.FUND_INVESTMENT_SUMMARY}
                  documentReferencesProps={{
                    currentPage: FUND_INVESTMENT_SUMMARY_REFERENCE_TITLE,
                    filesToSave,
                    isDisabled: isCurrentValuationDisabled,
                    referencedFeatureId: currentFundInvestmentOwnershipId,
                    referenceType: FUND_INVESTMENT_SUMMARY_REFERENCE_TYPE,
                    selectedMeasurementDate,
                    setFilesToSave,
                  }}
                  notesProps={{
                    isDisabled: isCurrentValuationDisabled,
                    notes,
                    onAddNote,
                    onDeleteNote,
                    onUpdateNotes,
                    pageType: FUND_INVESTMENT_SUMMARY_PAGE_VALUE,
                    pageTypeId: currentFundInvestmentOwnershipId,
                    pageTypeKey: FUND_INVESTMENT_SUMMARY_PAGE_KEY,
                    setNotes,
                  }}
                />
              )}
            </>
          )
        )}
      </Grid>
    </CustomContainer>
  );
};

export default FundInvestmentSummary;
